import { v4 as uuidv4 } from 'uuid';

/**
 * Security utilities for secure API operations
 */

export interface IdempotencyKeyInfo {
  key: string;
  timestamp: number;
  operation: string;
  userId?: string;
}

// Type guard for error objects with response property
interface ErrorWithResponse {
  response?: {
    data?: {
      error?: string;
      message?: string;
      retryAfter?: number;
    };
    status?: number;
  };
  code?: string;
  message?: string;
}

function isErrorWithResponse(error: unknown): error is ErrorWithResponse {
  return (
    error !== null &&
    typeof error === 'object' &&
    'response' in error
  );
}

/* The server's reply behind an error, whichever shape it arrived in. network.ts
 * throws an Error carrying `status` and `data` (its ApiError); an axios error
 * keeps them under `response`. Looking only under `response`, as
 * handleSecurityError used to, matched neither in practice — so every reason the
 * server gave (a daily limit, a 2FA failure, not enough cards) reached the member
 * as a bare "failed. Please try again." */
const replyOf = (error: unknown): { status?: number; data?: any } => {
  if (error === null || typeof error !== 'object') return {};
  const e = error as ErrorWithResponse & { status?: number; data?: any };
  return e.response
    ? { status: e.response.status, data: e.response.data }
    : { status: e.status, data: e.data };
};

/**
 * Generate a unique idempotency key for API requests
 */
export const generateIdempotencyKey = (operation: string, userId?: string): string => {
  const timestamp = Date.now();
  const uuid = uuidv4();
  const prefix = operation.toLowerCase().replace(/[^a-z0-9]/g, '_');
  
  // Create a deterministic but unique key
  return `${prefix}_${userId || 'user'}_${timestamp}_${uuid}`;
};

/**
 * Storage key for idempotency keys
 */
const IDEMPOTENCY_STORAGE_KEY = 'incrypt_idempotency_keys';

/**
 * Store idempotency key information for tracking
 */
export const storeIdempotencyKey = (keyInfo: IdempotencyKeyInfo): void => {
  try {
    const stored = localStorage.getItem(IDEMPOTENCY_STORAGE_KEY);
    const keys: IdempotencyKeyInfo[] = stored ? JSON.parse(stored) : [];
    
    // Add new key
    keys.push(keyInfo);
    
    // Clean up old keys (older than 24 hours)
    const dayAgo = Date.now() - (24 * 60 * 60 * 1000);
    const cleanedKeys = keys.filter(k => k.timestamp > dayAgo);
    
    localStorage.setItem(IDEMPOTENCY_STORAGE_KEY, JSON.stringify(cleanedKeys));
  } catch (error) {
    console.warn('Failed to store idempotency key:', error);
  }
};

/**
 * Check if an idempotency key has been used recently
 */
export const hasRecentIdempotencyKey = (operation: string, userId?: string): IdempotencyKeyInfo | null => {
  try {
    const stored = localStorage.getItem(IDEMPOTENCY_STORAGE_KEY);
    if (!stored) return null;
    
    const keys: IdempotencyKeyInfo[] = JSON.parse(stored);
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
    
    // Look for recent keys for the same operation and user
    const recentKey = keys.find(k => 
      k.operation === operation && 
      k.userId === userId && 
      k.timestamp > fiveMinutesAgo
    );
    
    return recentKey || null;
  } catch (error) {
    console.warn('Failed to check recent idempotency keys:', error);
    return null;
  }
};

/**
 * Security headers for API requests
 */
export const getSecurityHeaders = (idempotencyKey: string) => ({
  'idempotency-key': idempotencyKey,
  'X-Client-Version': '2.0.0',
  'X-Client-Platform': 'web',
  'X-Request-ID': uuidv4()
});

/**
 * Validate response for security issues
 */
export const validateSecurityResponse = (response: any): boolean => {
  // Check for security-related response fields
  if (response?.error === 'MAINTENANCE_MODE') {
    return false;
  }
  
  if (response?.error === 'RATE_LIMIT_EXCEEDED') {
    throw new Error(`Rate limit exceeded. ${response.message || 'Please wait before trying again.'}`);
  }
  
  if (response?.error === 'INVALID_IDEMPOTENCY_KEY') {
    throw new Error('Invalid request format. Please refresh and try again.');
  }
  
  if (response?.error === 'TRANSACTION_REPLAY_DETECTED') {
    throw new Error('This transaction has already been processed.');
  }
  
  return true;
};

/**
 * Enhanced error handler for security-related errors
 */
export const handleSecurityError = (error: unknown, operation: string): string => {
  console.error(`Security error in ${operation}:`, error);

  const { data } = replyOf(error);
  const errorCode: string | undefined =
    typeof data?.error === 'string' ? data.error : undefined;
  const message: string | undefined =
    typeof data?.message === 'string'
      ? data.message
      : typeof data?.error?.message === 'string'
        ? data.error.message
        : undefined;

  if (message && !errorCode) {
    return message;
  }

  // Handle specific security errors
  if (errorCode) {
    switch (errorCode) {
      case 'MAINTENANCE_MODE':
        // The server names the feature ("Digital Collectible withdrawals are
        // temporarily disabled…"); `operation` is an internal code (NFT_WITHDRAWAL).
        return (
          message ||
          `${operation} is temporarily disabled for maintenance. Please try again later.`
        );

      case 'RATE_LIMIT_EXCEEDED':
        const retryAfter = data.retryAfter;
        return `Too many requests. Please wait ${retryAfter ? retryAfter + ' seconds' : 'a moment'} before trying again.`;
        
      case 'INVALID_IDEMPOTENCY_KEY':
        return 'Request format error. Please refresh the page and try again.';
        
      case 'TRANSACTION_REPLAY_DETECTED':
        return 'This transaction has already been processed. Please check your balance.';
        
      case 'INSUFFICIENT_BALANCE':
        return 'Insufficient balance for this transaction.';
        
      case 'BLOCKCHAIN_CONFIRMATION_TIMEOUT':
        return 'Transaction is taking longer than expected. Please check your wallet and blockchain status.';
        
      default:
        return message || `${operation} failed due to a security error.`;
    }
  }
  
  const text = error instanceof Error ? error.message : '';

  // Handle network errors
  if (
    (error as { code?: string } | null)?.code === 'NETWORK_ERROR' ||
    text.includes('Network Error')
  ) {
    return 'Network connection error. Please check your internet connection and try again.';
  }

  // Handle wallet errors
  if (text.includes('WalletSignTransactionError') || text.includes('User rejected')) {
    return 'Transaction was cancelled or rejected by your wallet.';
  }

  // Default error message
  return `${operation} failed. Please try again.`;
};

/**
 * Generate request fingerprint for additional security
 */
export const generateRequestFingerprint = (operation: string, data: any): string => {
  const fingerprint = {
    operation,
    timestamp: Date.now(),
    userAgent: navigator.userAgent.substring(0, 100), // Truncate for privacy
    screen: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    dataHash: JSON.stringify(data).length // Simple hash of data size
  };
  
  return btoa(JSON.stringify(fingerprint));
};

/**
 * Delay function for rate limiting
 */
export const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Retry mechanism with exponential backoff
 */
export const retryWithBackoff = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry certain errors
      if (isErrorWithResponse(error) &&
          (error.response?.data?.error === 'INVALID_IDEMPOTENCY_KEY' ||
           error.response?.data?.error === 'TRANSACTION_REPLAY_DETECTED' ||
           error.response?.status === 400)) {
        throw error;
      }
      
      if (attempt === maxRetries) {
        break;
      }
      
      // Exponential backoff
      const delayMs = baseDelay * Math.pow(2, attempt - 1);
      console.log(`Attempt ${attempt} failed, retrying in ${delayMs}ms...`);
      await delay(delayMs);
    }
  }
  
  throw lastError;
};

/**
 * Check if browser supports required features
 */
export const checkBrowserCompatibility = (): boolean => {
  const requiredFeatures = [
    'localStorage' in window,
    'JSON' in window,
    'Promise' in window,
    'fetch' in window,
    'crypto' in window && 'getRandomValues' in window.crypto
  ];
  
  return requiredFeatures.every(feature => feature);
};

/**
 * Generate secure random string for additional entropy
 */
export const generateSecureRandom = (length: number = 16): string => {
  if (!checkBrowserCompatibility()) {
    // Fallback for older browsers
    return Math.random().toString(36).substring(2, 2 + length);
  }
  
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

export default {
  generateIdempotencyKey,
  storeIdempotencyKey,
  hasRecentIdempotencyKey,
  getSecurityHeaders,
  validateSecurityResponse,
  handleSecurityError,
  generateRequestFingerprint,
  delay,
  retryWithBackoff,
  checkBrowserCompatibility,
  generateSecureRandom
};
